/**
 * 최근검색어 크로스 도메인 공유를 위한 쿠키 유틸리티
 *
 * 전략: 2-Tier 저장소
 * - 쿠키(.myrealtrip.com): 경량 데이터 (id, title, timeStamp, groupType, isDomestic)
 * - localStorage: 풀 데이터 캐시 (위 + iconUrl, subTitle, version)
 */

import { getBrowserCookie } from './getBrowserCookie';

// ─── Types ───

/** 업데이트 시 accommodation > models/UnionDto > STAY_TYPE, main > models/VerticalHomeDto > STAY_TYPE, main > constants/accommodationHome > STAY_TYPE 동기화 필요 */
export const STAY_TYPE = {
  DOMESTIC: 'domestic',
  OVERSEAS: 'overseas',
  MINBAK: 'minbak',
  CASUAL_STAY: 'casual-stay',
  PENSION: 'pension',
} as const;

/** 업데이트 시 main > constants/accommodationHome > STAY_TYPE_FLAG 동기화 필요 */
export const STAY_TYPE_FLAG = {
  [STAY_TYPE.DOMESTIC]: 'd',
  [STAY_TYPE.OVERSEAS]: 'o',
  [STAY_TYPE.MINBAK]: 'm',
  [STAY_TYPE.CASUAL_STAY]: 'c',
  [STAY_TYPE.PENSION]: 'p',
} as const;

/** 업데이트 시 main > constants/accommodationHome > STAY_TYPE_FLAG 동기화 필요 */
export const STAY_TYPE_FLAG_MAP = {
  d: STAY_TYPE.DOMESTIC,
  o: STAY_TYPE.OVERSEAS,
  m: STAY_TYPE.MINBAK,
  c: STAY_TYPE.CASUAL_STAY,
  p: STAY_TYPE.PENSION,
} as const;

/** 쿠키 저장용 축약 구조 */
export interface CookieHistoryItem {
  i: number | null; // id
  t: string; // title
  s: number; // timeStamp
  g: 'R' | 'S' | null; // groupType: REGION→R, STAY→S
  d: '1' | '0'; // isDomestic: true→1, false→0
  st?: (typeof STAY_TYPE_FLAG)[keyof typeof STAY_TYPE_FLAG]; // stayType: domestic→d, overseas→o, minbak→m, casual-stay→c, pension→p
  ci?: string; // checkIn (YYYY-MM-DD)
  co?: string; // checkOut (YYYY-MM-DD)
  a?: number; // adultCount
  ca?: string; // childAges - "5.8.10" 형태 (쿠키 크기 최적화)
}

/** 앱에서 사용하는 풀 데이터 구조 (SearchHistoryItem 호환) */
export interface SearchHistoryEntry {
  version?: string;
  id: number | null;
  iconUrl?: string;
  title: string;
  subTitle?: string;
  timeStamp: number;
  groupType: ('REGION' | 'STAY') | null;
  isDomestic: 'true' | 'false';
  stayType?: (typeof STAY_TYPE)[keyof typeof STAY_TYPE];
  checkIn?: string;
  checkOut?: string;
  adultCount?: number;
  childAges?: number[];
}

// ─── Constants ───

const COOKIE_NAME = 'mrt-accom-sh';
const CACHE_KEY = 'accommodation-search-history-cache';
const LEGACY_KEY = 'accommodation-search-history';
const MIGRATION_FLAG = 'mrt-sh-migrated';
const MAX_ITEMS = 10;
const COOKIE_SOFT_LIMIT = 3000;
const COOKIE_HARD_LIMIT = 3800;
const COOKIE_MAX_AGE_DAYS = 30;
const CURRENT_VERSION = 'v2';

// ─── Cookie Domain ───

/**
 * 클라이언트용 쿠키 도메인 추출 (window.location.hostname 기반)
 * 참고: 서버사이드용 generateCookieDomain은 process.env.TARGET_ENV_PREFIX 기반으로 별도 존재
 */
function getCookieDomain(): string | undefined {
  if (typeof window === 'undefined') return undefined;

  const { hostname } = window.location;

  if (hostname === 'localhost' || /^(\d{1,3}\.){3}\d{1,3}$/.test(hostname)) {
    return undefined;
  }

  const parts = hostname.split('.');
  if (parts.length >= 2) {
    return `.${parts.slice(-2).join('.')}`;
  }

  return undefined;
}

// ─── Compact Conversion ───

function toCompact(item: SearchHistoryEntry): CookieHistoryItem {
  const compact: CookieHistoryItem = {
    i: item.id,
    t: item.title,
    s: item.timeStamp,
    g: item.groupType === 'REGION' ? 'R' : item.groupType === 'STAY' ? 'S' : null,
    d: item.isDomestic === 'true' ? '1' : '0',
  };
  if (item.stayType) compact.st = STAY_TYPE_FLAG[item.stayType];
  if (item.checkIn) compact.ci = item.checkIn;
  if (item.checkOut) compact.co = item.checkOut;
  if (item.adultCount !== undefined) compact.a = item.adultCount;
  if (item.childAges && item.childAges.length > 0) compact.ca = item.childAges.join('.');
  return compact;
}

function fromCompact(compact: CookieHistoryItem): SearchHistoryEntry {
  const entry: SearchHistoryEntry = {
    id: compact.i,
    title: compact.t,
    timeStamp: compact.s,
    groupType: compact.g === 'R' ? 'REGION' : compact.g === 'S' ? 'STAY' : null,
    isDomestic: compact.d === '1' ? 'true' : 'false',
  };

  if (compact.st) {
    entry.stayType = STAY_TYPE_FLAG_MAP[compact.st];
  }

  const datePattern = /^\d{4}-\d{2}-\d{2}$/;
  const today = new Date().toISOString().slice(0, 10);

  if (typeof compact.ci === 'string' && datePattern.test(compact.ci) && compact.ci >= today) {
    entry.checkIn = compact.ci;
  }
  if (typeof compact.co === 'string' && datePattern.test(compact.co) && compact.co >= today) {
    entry.checkOut = compact.co;
  }
  if (typeof compact.a === 'number' && compact.a > 0 && compact.a <= 20) {
    entry.adultCount = compact.a;
  }
  if (typeof compact.ca === 'string' && compact.ca.length > 0) {
    const ages = compact.ca
      .split('.')
      .map(Number)
      .filter((n) => !isNaN(n) && n >= 0 && n <= 17);
    if (ages.length > 0) entry.childAges = ages;
  }

  return entry;
}

// ─── Cookie CRUD (document.cookie 직접 조작) ───

function readCookie(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  return getBrowserCookie(COOKIE_NAME);
}

function writeCookie(value: string) {
  if (typeof document === 'undefined') return;

  const domain = getCookieDomain();
  const expires = new Date(Date.now() + COOKIE_MAX_AGE_DAYS * 24 * 60 * 60 * 1000).toUTCString();

  let cookieStr = `${COOKIE_NAME}=${value}; path=/; expires=${expires}; SameSite=Lax`;
  if (domain) cookieStr += `; domain=${domain}`;
  if (window.location.protocol === 'https:') cookieStr += '; Secure';

  document.cookie = cookieStr;
}

function deleteCookie() {
  if (typeof document === 'undefined') return;

  const domain = getCookieDomain();
  let cookieStr = `${COOKIE_NAME}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  if (domain) cookieStr += `; domain=${domain}`;

  document.cookie = cookieStr;
}

// ─── Cookie History Read/Write ───

function readCookieHistory(): SearchHistoryEntry[] {
  try {
    const raw = readCookie();
    if (!raw) return [];

    const decoded = decodeURIComponent(raw);
    const compacts: CookieHistoryItem[] = JSON.parse(decoded);

    if (!Array.isArray(compacts)) {
      deleteCookie();
      return [];
    }

    return compacts.map(fromCompact);
  } catch {
    deleteCookie();
    return [];
  }
}

function writeCookieHistory(items: SearchHistoryEntry[]) {
  try {
    const compacts = items.slice(-MAX_ITEMS).map(toCompact);
    const encoded = encodeURIComponent(JSON.stringify(compacts));

    if (encoded.length > COOKIE_HARD_LIMIT) {
      // 하드 리밋 초과 시 오래된 항목부터 제거
      const trimmed = ensureCookieSize(compacts);
      if (!trimmed) return; // 1개로도 초과 → 쿠키 쓰기 포기
      writeCookie(encodeURIComponent(JSON.stringify(trimmed)));
      return;
    }

    if (encoded.length > COOKIE_SOFT_LIMIT) {
      console.warn(`[searchHistoryCookie] 쿠키 크기 경고: ${encoded.length}bytes`);
    }

    writeCookie(encoded);
  } catch {
    // 쿠키 쓰기 실패 → 무시 (localStorage 폴백)
  }
}

function ensureCookieSize(items: CookieHistoryItem[]): CookieHistoryItem[] | null {
  const trimmed = [...items];

  // 1단계: 가장 오래된 항목부터 검색 옵션 필드만 제거
  for (let idx = 0; idx < trimmed.length; idx++) {
    const encoded = encodeURIComponent(JSON.stringify(trimmed));
    if (encoded.length <= COOKIE_HARD_LIMIT) return trimmed;
    delete trimmed[idx].ci;
    delete trimmed[idx].co;
    delete trimmed[idx].a;
    delete trimmed[idx].ca;
  }

  // 2단계: 그래도 초과하면 항목 자체 제거 (기존 로직)
  while (trimmed.length > 0) {
    const encoded = encodeURIComponent(JSON.stringify(trimmed));
    if (encoded.length <= COOKIE_HARD_LIMIT) return trimmed;
    trimmed.shift();
  }

  return null;
}

// ─── LocalStorage Cache ───

function readCacheHistory(): SearchHistoryEntry[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeCacheHistory(items: SearchHistoryEntry[]) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(items));
  } catch {
    // silent
  }
}

// ─── Legacy LocalStorage Read ───

function readLegacyHistory(): SearchHistoryEntry[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(LEGACY_KEY);
    if (!raw) return [];
    const items = JSON.parse(raw);
    if (!Array.isArray(items)) return [];
    return items.filter(
      (i: SearchHistoryEntry) =>
        (!i.version || i.version === CURRENT_VERSION) &&
        Boolean(i.title) &&
        // 프리텍스트 검색어(id·groupType 모두 없음)는 복원하지 않음
        !(i.id === null && !i.groupType),
    );
  } catch {
    return [];
  }
}

// ─── Merge & Dedup ───

function mergeHistories(
  primary: SearchHistoryEntry[],
  secondary: SearchHistoryEntry[],
): SearchHistoryEntry[] {
  const merged = [...primary];
  const seen = new Set(primary.map((item) => itemKey(item)));

  for (const item of secondary) {
    const key = itemKey(item);
    if (!seen.has(key)) {
      seen.add(key);
      merged.push(item);
    }
  }

  return merged.sort((a, b) => a.timeStamp - b.timeStamp).slice(-MAX_ITEMS);
}

function itemKey(item: SearchHistoryEntry): string {
  if (item.id !== null) return `id:${item.id}`;
  return `title:${item.title}:${item.isDomestic}`;
}

// ─── Enrich (쿠키 데이터에 localStorage 캐시의 iconUrl/subTitle 보강) ───

function enrichFromCache(
  cookieItems: SearchHistoryEntry[],
  cacheItems: SearchHistoryEntry[],
): SearchHistoryEntry[] {
  const cacheMap = new Map<string, SearchHistoryEntry>();
  for (const item of cacheItems) {
    cacheMap.set(itemKey(item), item);
  }

  return cookieItems.map((item) => {
    const cached = cacheMap.get(itemKey(item));
    if (cached) {
      return {
        ...item,
        iconUrl: item.iconUrl || cached.iconUrl,
        subTitle: item.subTitle || cached.subTitle,
        version: cached.version || CURRENT_VERSION,
      };
    }
    return { ...item, version: CURRENT_VERSION };
  });
}

// ─── Public API ───

/**
 * 최근검색 히스토리 읽기
 * 쿠키(진실의 원천) → localStorage 캐시에서 iconUrl/subTitle 보강
 * 쿠키가 비어있으면 legacy localStorage 폴백
 */
export function readSearchHistory(): SearchHistoryEntry[] {
  const cookieItems = readCookieHistory();
  const cacheItems = readCacheHistory();

  if (cookieItems.length > 0) {
    return enrichFromCache(cookieItems, cacheItems);
  }

  // 쿠키가 비어있으면 legacy localStorage 폴백 (아직 미이관된 경우)
  const legacyItems = readLegacyHistory();
  return legacyItems.slice(-MAX_ITEMS);
}

/**
 * 최근검색 히스토리 쓰기
 * 쿠키(경량) + localStorage 캐시(풀) 동시 기록
 * + legacy localStorage에도 Dual-Write (배포 과도기 대응)
 */
export function writeSearchHistory(items: SearchHistoryEntry[]) {
  const sliced = items.slice(-MAX_ITEMS);

  // 쿠키에 경량 데이터 저장
  writeCookieHistory(sliced);

  // localStorage 캐시에 풀 데이터 저장
  writeCacheHistory(sliced);

  // legacy localStorage에도 Dual-Write (다른 앱이 아직 구버전일 수 있음)
  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem(LEGACY_KEY, JSON.stringify(sliced));
    } catch {
      // silent
    }
  }
}

/**
 * 기존 localStorage 데이터를 쿠키로 1회 이관
 * 이미 쿠키에 데이터가 있으면 병합 처리
 */
export function migrateFromLocalStorage() {
  if (typeof window === 'undefined') return;

  try {
    const migrated = localStorage.getItem(MIGRATION_FLAG);
    if (migrated) return;

    const legacyItems = readLegacyHistory();
    if (legacyItems.length === 0) {
      localStorage.setItem(MIGRATION_FLAG, '1');
      return;
    }

    const cookieItems = readCookieHistory();
    const merged = mergeHistories(cookieItems, legacyItems);

    writeCookieHistory(merged);
    writeCacheHistory(merged);

    localStorage.setItem(MIGRATION_FLAG, '1');
  } catch (e) {
    console.warn('[searchHistoryCookie] 마이그레이션 실패, 다음 방문 시 재시도:', e);
  }
}

export { COOKIE_NAME, LEGACY_KEY, CACHE_KEY, MIGRATION_FLAG, MAX_ITEMS, CURRENT_VERSION };
